import React from 'react'
import { Space, Button, notification, Modal } from 'antd'

import forge from 'node-forge'
import { Buffer } from 'buffer'
import { safeboxClient } from 'myNet'
import store from 'store'
import i18n from 'i18n'
import SettingsService from 'popups/Settings/api/Settings'
import { setEnvParam, setTempEnvParam, getTempEnvParam, removeTempEnvParam, getEnvParam } from 'env'
import {
  readVaultWriteFormat,
  encryptVaultBlob,
  VAULT_WRITE_FORMAT_KEY,
  VAULT_KDF_NONE,
  VAULT_KDF_PBKDF2,
} from 'vaultWriteFormat'

import { history, store as reduxStore } from 'index'
import { buildEscrow, buildEscrowedKeysForWorkspaces } from 'recoveryCrypto'

import { errorBase } from 'utils/ui'
import { handleAccessBlocked } from 'services/jwt'
import { removeLocalAppUserKeys } from 'utils/localUserKeys'
import { decryptVaultBlob } from 'services/vaultV2/format'
import { SERVICE_TITLE } from 'service'
import { captureSentryError } from 'services/restClient'
import exclamationIcon from '@/assets/images/circle-blue-22.svg'

const webCryptoPbkdf2 = async (password, salt, numIterations, keySize) => {
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    Buffer.from(password, 'binary'),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey'],
  )
  const keyAsArrayBuffer = await window.crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: Buffer.from(salt, 'binary'),
      iterations: numIterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    keySize << 3, // eslint-disable-line no-bitwise
  )

  const key = new forge.util.ByteBuffer(keyAsArrayBuffer).getBytes()
  // console.log(' --- key : ', key)

  return key
}

async function generateKey(password, salt, numIterations, keySize) {
  const numIterations0 = numIterations || 100000
  const keySize0 = keySize || 32

  const key = await webCryptoPbkdf2(password, salt, numIterations0, keySize0)
  return key
}

function encryptAES(data, key) {
  const iv = forge.random.getBytesSync(16)

  // Note: a key size of 16 bytes will use AES-128, 24 => AES-192, 32 => AES-256
  const cipher = forge.cipher.createCipher('AES-CBC', key)
  cipher.start({ iv })
  const bytes = new forge.util.ByteBuffer(data)
  cipher.update(bytes)
  cipher.finish()

  const encrypted = cipher.output
  // console.log(encrypted.toHex());
  const encBytes = encrypted.getBytes()
  const ivBytes = forge.util.createBuffer(iv).getBytes()

  return ivBytes + encBytes
}

function decryptAES(ciphertext, key) {
  const ivStr = ciphertext.slice(0, 16) // IV 추출
  const iv = forge.util.createBuffer(ivStr)

  const decipher = forge.cipher.createDecipher('AES-CBC', key)
  decipher.start({ iv })
  const bytes = new forge.util.ByteBuffer(ciphertext.slice(16))
  decipher.update(bytes)
  // finish()=false(PKCS#7 패딩 불일치) = 잘못된 키 또는 손상 데이터. 반환값을 무시하면
  // 쓰레기 평문이 조용히 전파되어 재암호화 업로드 경로(changePW/admin recovery)에서
  // 서버 데이터를 영구 오염시킨다 (2026-06-09 dev user_key 오염 사고 근본 원인).
  if (!decipher.finish()) {
    throw new Error('AES decrypt failed: invalid padding (wrong key or corrupted data)')
  }

  const decrypted = decipher.output
  const encBytes = decrypted.getBytes()

  return encBytes
}

export async function encryptAESByKey(data, key, numIterations) {
  const salt = forge.random.getBytesSync(16)
  const enKey = await generateKey(key, salt, numIterations)
  const encryptedBytes = encryptAES(data, enKey)
  return salt + encryptedBytes
}

export async function decryptAESByKey(ciphertext, key, numIterations) {
  // v1/v2 판별은 wrapper 가 한다 (설계 §7 2단계 판정). **시그니처는 바뀌지 않는다** —
  // v2 는 반복 횟수를 헤더에서 읽으므로 호출처 10곳이 그대로 통한다.
  // 아래 thunk 가 구포맷 경로이고, 에러 타입·메시지를 유지한다(`decryptAES:84-86`).
  return decryptVaultBlob(ciphertext, key, async () => {
    const salt = ciphertext.slice(0, 16) // IV 추출
    const enKey = await generateKey(key, salt, numIterations)
    return decryptAES(ciphertext.slice(16), enKey)
  })
}

export async function encryptBase64ByPassword(plainText, password) {
  const utf8 = forge.util.encodeUtf8(plainText)
  const encryptedBytes = await encryptAESByKey(utf8, password)
  return forge.util.encode64(encryptedBytes)
}

export async function decryptBase64ByPassword(base64Str, password) {
  const encryptedBytes = forge.util.decode64(base64Str)
  const decryptedBytes = await decryptAESByKey(encryptedBytes, password)
  return forge.util.decodeUtf8(decryptedBytes)
}

const UMKInfo = {
  // 모든 환경에서 메모리만 저장 → 새로고침 시 UMK 재입력 요구 (운영과 dev 동작 통일)
  useTempEnv: true,
  mouseMoveTimer: null,
  mouseMoveCnt: 0,
}

// if (process.env.REACT_APP_TARGET === 'production') {
//   UMKInfo.useTempEnv = true
// }

if (UMKInfo.useTempEnv === true) {
  UMKInfo.set = (key, value) => {
    setTempEnvParam(key, value)
  }
  UMKInfo.get = (key) => {
    return getTempEnvParam(key)
  }
  UMKInfo.remove = (key) => {
    removeTempEnvParam(key)
  }
} else {
  UMKInfo.set = (key, value) => {
    store.set(key, value)
  }
  UMKInfo.get = (key) => {
    return store.get(key)
  }
  UMKInfo.remove = (key) => {
    store.remove(key)
  }
}

export function setUMKOptions(options) {
  if (UMKInfo.opts == null) {
    UMKInfo.opts = {
      loopTime: 30 * 1000, // 30초마다 체크.
      duration: 10 * 60 * 1000, // 10분 동안 유효.
      warningDuration: 1 * 60 * 1000, // 만료 전에 경고창 표시 시간.
      expiredMsg: 'UMK expired',
      warningMsg: 'UMK will be expired in 1 minute',
    }
  }

  UMKInfo.opts = {
    ...UMKInfo.opts,
    ...options,
  }
}

function saveUMKData(UMK, expireTime) {
  const umkData = {
    UMK,
    expireTime,
  }
  const umkDataStr = JSON.stringify(umkData)

  if (process.env.REACT_APP_TARGET === 'production') {
    const reduxState = reduxStore.getState()
    const { email } = reduxState.user
    const decEmail = email?.length > 0 ? email : store.get('app.user.email')
    encryptAESByKey(umkDataStr, decEmail, 10000).then((encData) => {
      UMKInfo.set('app.user.encUMK', encData)
    })
  } else {
    UMKInfo.set('app.user.encUMK', umkDataStr)
  }
}

export function setUMKToLocal(UMK, options, isConfirm = true) {
  setUMKOptions(options)

  UMKInfo.startCheck = () => {
    if (UMKInfo.checkExpiredTimer != null) {
      clearTimeout(UMKInfo.checkExpiredTimer)
    }

    const { duration, warningDuration, loopTime } = UMKInfo.opts

    UMKInfo.warningOpened = false
    UMKInfo.expiredTime = duration === 0 ? 0 : Date.now() + duration
    UMKInfo.warningTime = warningDuration > 0 ? Date.now() + duration - warningDuration : 0

    saveUMKData(UMK, UMKInfo.expiredTime)

    UMKInfo.checkExpired = () => {
      const { expiredTime, warningTime } = UMKInfo
      const curTime = Date.now()

      console.log(' --- remain time : ', (expiredTime - curTime) / 1000)

      let nextInterval = 0
      if (warningTime > 0 && expiredTime > warningTime) {
        if (curTime < warningTime) {
          nextInterval = warningTime - curTime
        } else if (curTime < expiredTime) {
          nextInterval = expiredTime - curTime
        }
      } else if (curTime < expiredTime) {
        nextInterval = expiredTime - curTime
      }

      if (nextInterval > 0) {
        nextInterval = Math.min(nextInterval, loopTime)
        nextInterval = Math.max(nextInterval, 1000)

        if (
          warningTime > 0 &&
          curTime >= warningTime &&
          curTime < expiredTime &&
          UMKInfo.warningOpened !== true
        ) {
          const time = (expiredTime - curTime) / 1000
          console.log(' --- warning', time)

          const key = `open${Date.now()}`
          const btn = (
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  UMKInfo.startCheck()
                  notification.destroy(key)
                }}
              >
                Extend time
              </Button>
            </Space>
          )

          notification.open({
            key,
            message: 'Warning',
            description: UMKInfo.opts.warningMsg,
            btn,
            showProgress: true,
            duration: time,
          })

          UMKInfo.warningOpened = true
        }

        UMKInfo.checkExpiredTimer = setTimeout(() => {
          UMKInfo.checkExpired()
        }, nextInterval)
      } else {
        Modal.destroyAll()
        const curPath = window.location.pathname

        if (isConfirm === true) {
          notification.warning({
            icon: <img src={exclamationIcon} alt="exclamation" />,
            message: i18n.t('common.warning.securityLock'),
            description: i18n.t('common.warning.securityLockMsg'),
          })
        }

        deleteUMKFromLocal()

        const pureLoginPath = '/masterPW/login'
        // auto-lock(유휴 만료)으로 인한 리다이렉트임을 LoginMasterPW 에 전달 — 진입 화면이
        // 자동 패스키 세리머니 대신 버튼 대기(사용자 제스처 인증)로 분기하기 위함. WebAuthn Modal 은
        // 타임아웃이 있어 사용자 부재 시 자동 호출하면 응답 없이 타임아웃 에러로 떨어진다.
        const loginPath = `/masterPW/login?path=${curPath}&reason=autolock`
        if (curPath === pureLoginPath) {
          // 이 분기(이미 로그인 화면)는 UMK 보유 상태에서만 도는 유휴 타이머에 의해 도달하지 않으므로
          // reason=autolock 을 붙이지 않는다 (UMK 가 null 인 로그인 화면에서는 타이머 미동작).
          history.push(pureLoginPath)
          window.addEventListener('popstate', () => {
            history.push(pureLoginPath)
          })
        } else {
          history.push(loginPath)
          window.addEventListener('popstate', () => {
            history.push(loginPath)
          })
        }
      }
    }

    if (duration > 0) {
      UMKInfo.checkExpired()
    }
  }

  UMKInfo.startCheck()
}

export function notifyMouseMove() {
  if (UMKInfo.warningOpened !== true && UMKInfo.checkExpiredTimer != null) {
    if (UMKInfo.mouseMoveTimer == null) {
      console.log(' --- notifyMouseMove', UMKInfo.warningOpened)
      console.log(' --- startCheck')
      if (UMKInfo.startCheck != null) {
        UMKInfo.startCheck()
      }
      UMKInfo.mouseMoveCnt = 0
      // 10초 마다 한번만 timer reset.
      UMKInfo.mouseMoveTimer = setTimeout(() => {
        UMKInfo.mouseMoveTimer = null
        if (UMKInfo.mouseMoveCnt > 0) {
          console.log(' --- startCheck2')
          UMKInfo.mouseMoveTimer = null
          if (UMKInfo.startCheck != null) {
            UMKInfo.startCheck()
          }
        }
      }, 10000)
    } else {
      UMKInfo.mouseMoveCnt += 1
    }
  }
}

export function deleteUMKFromLocal() {
  if (UMKInfo.mouseMoveTimer != null) {
    clearTimeout(UMKInfo.mouseMoveTimer)
    UMKInfo.mouseMoveTimer = null
  }

  if (UMKInfo.checkExpiredTimer != null) {
    clearTimeout(UMKInfo.checkExpiredTimer)
    UMKInfo.checkExpiredTimer = null
  }

  UMKInfo.remove('app.user.encUMK')
}

// 세션 UMK 세대 불일치(서버 845) 시 마스터 비밀번호 재입력 유도 — UMK 만료(securityLock) 흐름과 동일.
// 로컬 UMK 캐시를 지우고 잠금해제 화면으로 보내 재입력 → UMK_LOGIN 이 서버 기준으로 UMK 재검증·신선화한다.
// (recovery-option 전환/복구키 생성 시 stale enc_umk 오염 차단 흐름의 클라이언트 회복 경로)
export function redirectToMasterPwReenter() {
  deleteUMKFromLocal()
  const curPath = window.location.pathname
  history.push(`/masterPW/login?path=${curPath}`)
}

export async function getUMKFromLocal(resetExpireTimer) {
  const encUMKData = UMKInfo.get('app.user.encUMK')

  if (encUMKData == null) {
    return null
  }

  let umkDataStr = null
  if (process.env.REACT_APP_TARGET === 'production') {
    const reduxState = reduxStore.getState()
    const { email } = reduxState.user
    umkDataStr = await decryptAESByKey(encUMKData, email, 10000)
  } else {
    umkDataStr = encUMKData
  }

  const umkData = JSON.parse(umkDataStr)

  const { UMK, expireTime } = umkData

  if (expireTime < Date.now() && expireTime !== 0) {
    deleteUMKFromLocal()
    return null
  }

  if (resetExpireTimer === true && UMKInfo.startCheck != null && expireTime !== 0) {
    UMKInfo.startCheck()
  }

  return UMK
}

export function hashString(str) {
  const md = forge.md.sha256.create()
  md.update(str)
  return md.digest().toHex()
}

const oaepOpts = {
  md: forge.md.sha256.create(),
  mgf1: forge.mgf1.create(forge.md.sha256.create()),
}

export const decryptFromBase64 = (encryptedMessageBase64, privateKeyPem) => {
  const encryptedBytes = forge.util.decode64(encryptedMessageBase64) // Base64 디코딩
  const privateKey = forge.pki.privateKeyFromPem(privateKeyPem) // PEM 형식의 비밀 키 불러오기
  const decrypted = privateKey.decrypt(encryptedBytes, 'RSA-OAEP', oaepOpts)
  return decrypted
}

// 개인키 재암호화 업로드 직전 평문 sanity 검증 (user_key_corruption_guards §3.2).
// 패딩 검사를 우연히 통과한 잘못된 복호 결과가 서버에 업로드되어 키를 영구 오염시키는
// 2026-06-09 사고 경로의 2차 방어 — 업로드 경로(changePW/admin recovery)에만 적용.
export function assertPemPrivateKey(pem, context) {
  // 검사만 trim — 선행 공백/개행이 있는 정상 키의 false-reject 방지 (gemini 리뷰 #1307).
  // 반환은 원본 유지: 호출측이 이 값을 그대로 재암호화하므로 변형하지 않는다.
  if (typeof pem !== 'string' || !pem.trim().startsWith('-----BEGIN')) {
    throw new Error(`${context}: decrypted private key is not PEM — key material mismatch`)
  }
  return pem
}

// 인증 응답(login/register)의 복구 공개키를 tempEnv에 동기화 (user_key_corruption_guards §3.3a).
// 자발적 changePW가 new_enc_umk(=encrypt(새 UMK, 복구 공개키))를 만들 수 있게 한다.
// 'in' 검사: changePW 응답에는 이 키 자체가 없음(undefined) → no-op로 기존 값 유지(연속 변경 보존).
// 값이 null(NONE 사용자)이면 remove — SPA 내 계정 전환 시 이전 계정의 공개키 잔존으로
// 다른 계정의 enc_umk를 잘못된 키로 봉인하는 사고 차단.
export function syncRecoveryPubKeyFromAuthData(safeboxData) {
  // typeof 가드: 원시 타입(문자열 등)에 'in' 연산자는 TypeError (gemini 리뷰 #1307)
  if (safeboxData && typeof safeboxData === 'object' && 'recovery_pub_key' in safeboxData) {
    if (safeboxData.recovery_pub_key) {
      setTempEnvParam('base64recoveryPublicKey', safeboxData.recovery_pub_key)
      // 캐시 소유 계정 태그 — base64recoveryPublicKey 는 모듈 전역 tempEnv 라 SPA 계정전환에서
      // 이전 계정 값이 남을 수 있다(2026-06-25 사고). 동기화 시점의 계정을 함께 기록해 두면,
      // changePW 가 다른 계정의 stale 복구키로 enc_umk 를 잘못 봉인하는 것을 막을 수 있다.
      setTempEnvParam(
        'recoveryPubKeyOwner',
        safeboxData.user_email || store.get('app.user.email') || getEnvParam('userEmail') || null,
      )
    } else {
      removeTempEnvParam('base64recoveryPublicKey')
      removeTempEnvParam('recoveryPubKeyOwner')
    }
  }
}

export function RSAEncDec(options) {
  forge.options.usePureJavaScript = false
  console.log(' ---- forge option : ', forge.options)

  const self = this

  if (options == null) {
    throw new Error('options is empty.')
  }

  self.keySize = 4096 // 키 사이즈 설정 (2048비트)
  self.maxMessageLength = 446 // RSA로 암호화할 수 있는 최대 메시지 길이 (키 사이즈에 따라 다름)

  // password 초기화.
  const initUMK = async () => {
    if (options.umk != null) {
      self.umk = options.umk
    } else {
      self.umk = await getUMKFromLocal()
    }

    if (self.umk == null) {
      throw new Error('UMK not found')
    }
  }

  self.UMK = () => {
    return self.umk
  }

  if (options.isGroup === true) {
    self.getGroupKey = () => {
      return self.umk
    }
  }

  self.UMKHash = () => {
    const decryptedUMK = getTempEnvParam('decryptedUMK')
    // const resetPW = yield select((state) => state.user.resetPW)

    return hashString(decryptedUMK || self.UMK())
  }

  self.clear = () => {
    self.umk = null
    self.publicKeyPem = null
    self.privateKeyPem = null
  }

  self.publicKeyPem = null
  self.privateKeyPem = null

  // 공개 키와 비밀 키 생성
  const generateRSAKey = () => {
    return new Promise((resolve, reject) => {
      forge.pki.rsa.generateKeyPair({ bits: self.keySize, workers: 2 }, (err, keypair) => {
        if (err) {
          reject(err)
        } else {
          const publicKeyPem = forge.pki.publicKeyToPem(keypair.publicKey) // 공개 키 PEM 형식으로 변환
          const privateKeyPem = forge.pki.privateKeyToPem(keypair.privateKey) // 비밀 키 PEM 형식으로 변환
          resolve({ publicKeyPem, privateKeyPem })
        }
      })
    })
  }

  // 공개 키를 사용해 메시지 암호화
  const encryptToBase64 = (message, publicKeyPem) => {
    const publicKey = forge.pki.publicKeyFromPem(publicKeyPem || self.publicKeyPem) // PEM 형식의 공개 키 불러오기
    const encrypted = publicKey.encrypt(message, 'RSA-OAEP', oaepOpts)
    return forge.util.encode64(encrypted) // Base64로 인코딩하여 반환
  }

  self.init = async () => {
    await initUMK()

    let ret = null
    if (options.inviteRegisterLogin === true) {
      ret = await self.loginByInvite()
    } else if (options.isRegister === true) {
      ret = await self.register()
    } else if (options.isLogin === true) {
      ret = await self.login()
    } else if (options.isGroup === true) {
      ret = await self.registerGroup()
    }

    return ret
  }

  const sendSafeboxData = (safeboxData) => {
    // 복구 공개키 동기화 — 호출처 4곳(login/register×2/changePW) 공통 통로에서 1회 처리
    syncRecoveryPubKeyFromAuthData(safeboxData)

    const {
      user_id: userId,
      role,
      user_email: email,
      user_name: name,
      user_uuid: userUuid,
    } = safeboxData

    const safebox = {
      userId,
      role,
      email,
      name,
    }

    setEnvParam('appType', JSON.stringify(safebox))

    // Phase 2.5: common-auth user_uuid를 authUuid로 저장 → 이후 요청의 fingerprint는 hashString(authUuid).
    // 마이그레이션 미완료 user는 서버가 null을 반환할 수 있으므로 truthy check.
    if (userUuid) {
      store.set('app.user.authUuid', userUuid)
    }

    reduxStore.dispatch({
      type: 'user/SET_STATE',
      payload: {
        safebox,
      },
    })

    reduxStore.dispatch({
      type: 'menu/RELOAD',
    })
  }

  self.login = async () => {
    try {
      // Passkey fast path: 호출자가 /v1/auth/passkey/login 응답을 이미 받아 전달한 경우
      // 동일 구조의 응답이라 /v1/auth/login 호출 중복 (preflight + POST RTT) 절약.
      // backend verify_login 이 User.auth_login 을 그대로 호출하여 부수효과/응답 모두 동일.
      // fast path 에서는 보안상 응답 로깅 안 함 (data 에 암호화된 user_key 포함 — #1148 리뷰).
      let code
      let data
      if (options.preloadedLoginData) {
        code = options.preloadedLoginData.code
        data = options.preloadedLoginData.data
      } else {
        // Phase 2.5: authUuid가 있으면 SHA256(uuid)로 fingerprint 생성. 없으면 과도기 fallback으로 SHA256(email).
        // 백엔드 auth_login의 2순위 dual check가 SHA256(uuid)/SHA256(email) 둘 다 허용하므로 양쪽 모두 통과.
        const reduxState = reduxStore.getState()
        const { email } = reduxState.user
        const authUuid = store.get('app.user.authUuid')
        const params = {
          fingerprint: hashString(authUuid || email || store.get('app.user.email')),
          hmk: self.UMKHash(),
          // 감사 라벨 전용. 미지정이면 **키 자체를 보내지 않는다** — 백엔드가 기존 표기
          // (lg_logged_in)를 유지하고, 구버전 백엔드와도 호환된다.
          ...(options.unlockMethod ? { unlock_method: options.unlockMethod } : {}),
        }
        const umkRes = await safeboxClient.post(`/v1/auth/login`, params)
        code = umkRes.data.code
        data = umkRes.data.data
        console.log('umkInfo - ', code, data)
      }

      if ([870, 879].includes(code)) {
        // 차단 응답 — 온보딩(마스터PW 설정) 오진행 차단. LOGOUT saga는 SAML 하드 리다이렉트
        // 경합 때문에 스킵하고, 다른 계정 재시도를 위해 로컬 토큰만 정리 (QA round1 설계 §2)
        handleAccessBlocked(code, { skipLogout: true })
        removeLocalAppUserKeys()
        const blockedError = new Error('access_blocked')
        blockedError.code = 'ACCESS_BLOCKED'
        throw blockedError
      }

      if (code === 200) {
        const { role, user_id: userId, user_public_key: userPublicKey, user_key: userKey } = data
        // 서버가 지시한 저장 포맷 — 키를 준 **그 응답**에서 함께 꺼낸다 (설계 §3).
        // 웹은 저장 시 서버를 부르지 않으므로(:1332-1336 이 메모리 키를 쓴다) 여기서 보관한다.
        setTempEnvParam(VAULT_WRITE_FORMAT_KEY, readVaultWriteFormat(data))
        console.log('/v1/auth/login - ', data)
        sendSafeboxData(data)
        console.log('umkInfo - ', role, userId, userPublicKey, userKey)

        self.publicKeyPem = userPublicKey

        // 🛑 **금고 스윕이 CAS 의 `prev` 로 쓴다** (PR 7-b, 설계 vault_sweep_keys §2.3 K2).
        //    이 값을 안 남기면 스윕이 `user_key` 를 대상으로 만들 수 없다 — 다시 받으려면
        //    매 언락마다 `users/me?include_sensitive` 왕복이 되고, 그건 7-a-2 가 고친 F11 과
        //    같은 형태의 결함이다. **평문 개인키가 바로 아래에서 이미 self 에 남으므로
        //    암호문 추가 보관의 한계 비용은 0 이다.**
        self.encPrivateKeyBase64 = userKey
        self.userKeyId = data.user_key_id ?? null
        self.sweepKeys = data.sweep_keys === true

        const decPrivateKey = forge.util.decode64(userKey)
        self.privateKeyPem = await decryptAESByKey(decPrivateKey, self.UMK())

        const saveOptions = {}
        if (options.duration != null) {
          saveOptions.duration = options.duration
        }
        if (options.warningDuration != null) {
          saveOptions.warningDuration = options.warningDuration
        }
        setUMKToLocal(self.UMK(), saveOptions)

        // WRKP silent flow 트리거 (fire-and-forget, 메인 화면 진입 차단 X)
        triggerWrkpSilentFlows({
          wrkpPublicKeys: data.wrkp_public_keys,
          wrkpAdminStatus: data.wrkp_admin_status,
          umk: self.UMK(),
          privateKeyPem: self.privateKeyPem,
          userRole: role,
          context: 'login',
        })

        return Promise.resolve(true)
      }

      if (code === 780) {
        return Promise.resolve('expired')
      }
    } catch (e) {
      // 접근 차단(870/879) 마커는 code 프로퍼티 보존 필요 — new Error(e) 재포장 시 유실되어
      // landing.umkLogin의 ACCESS_BLOCKED 가드가 무력화된다 (PR #1566 리뷰)
      if (e?.code === 'ACCESS_BLOCKED') {
        throw e
      }
      console.log(' - umkLogin error : ', e)
      throw new Error(e)
    }

    return Promise.resolve(false)
  }

  self.loginByInvite = async () => {
    const inviteToken = store.get('app.user.invite_token')

    try {
      const params = await self.registerParams()

      console.log('params - ', params)

      const umkRes = await safeboxClient.post(
        `/v1/users/workspace-invite/${inviteToken}/register`,
        params,
      )
      const { code, register_result: registerResult } = umkRes.data
      const { data } = registerResult
      console.log('umkInfo - ', code, registerResult)
      if (code === 200) {
        const {
          role,
          user_id: userId,
          user_public_key: userPublicKey,
          user_key: userKey,
        } = data || {}

        console.log('/v1/users/workspace-invite/register - ', data)
        sendSafeboxData(data)
        console.log('umkInfo - ', role, userId, userPublicKey, userKey)

        const saveOptions = {}
        if (options.duration != null) {
          saveOptions.duration = options.duration
        }
        if (options.warningDuration != null) {
          saveOptions.warningDuration = options.warningDuration
        }

        const locale = store.get('app.settings.locale')

        await SettingsService.put({
          local: locale ? locale.substring(0, 2) : 'en',
          autoLock: '10',
          recoveryKeyToEmail: false,
        })

        const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')
        store.remove('app.user.invite_token')

        store.set('app.user.workspace_id', inviteType.workspace_id)
        setUMKToLocal(self.UMK(), saveOptions)

        // ESCROW 초대 가입 escrow backfill — register 시점엔 미가입이라 escrow=null 로 나갔으므로,
        // 가입 완료로 멤버가 된 지금 응답의 wrkp_public_keys 로 escrow 를 만들어 저장한다.
        await self.backfillRegisterEscrow(data?.wrkp_public_keys)

        // WRKP silent flow 트리거 — invite register 응답은 register_result.data nesting
        triggerWrkpSilentFlows({
          wrkpPublicKeys: data?.wrkp_public_keys,
          wrkpAdminStatus: data?.wrkp_admin_status,
          umk: self.UMK(),
          privateKeyPem: self.privateKeyPem,
          userRole: data?.role,
          context: 'loginByInvite',
        })

        return Promise.resolve(true)
      }

      const err = new Error(umkRes.data?.message)
      err.code = code
      return Promise.reject(err)
    } catch (e) {
      console.log(' - umkLoginByInvite error : ', e)
      throw e
    }
  }

  function savePrivateKeyLocally(pem) {
    const blob = new Blob([pem], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    // const name = store.get('app.user.name')
    const { name } = reduxStore.getState().user

    const a = document.createElement('a')
    a.href = url
    a.download =
      name != null
        ? `${name}_${SERVICE_TITLE}_Recovery_Key.pem`
        : `${SERVICE_TITLE}_Recovery_Key.pem`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    console.log('✅ RSA 개인키 다운로드 완료 (recovery_key.pem)')
  }

  self.createRecoveryKey = async () => {
    await initUMK()
    const { publicKeyPem, privateKeyPem } = await generateRSAKey()

    const encUMKForRecovery = encryptToBase64(self.UMK(), publicKeyPem)

    // 복구 개인키(PEM)는 항상 로컬 다운로드만 — 메일 발송 경로 제거 (2026-06-11 결정).
    // 이전엔 레거시 설정(recoverKeyEnabled='email')이 잔존하면 다운로드 없이 메일만 발송되어
    // "생성 완료 토스트 + 파일 없음" 증상 발생. 메일 발송용이던 recovery_priv_key 서버 전송도
    // 제거 — 개인키가 기기 밖으로 나가지 않는다(Zero-Knowledge 강화).
    const params = {
      enc_umk: encUMKForRecovery,
      recovery_pub_key: publicKeyPem,
      // 세션 UMK 신선도 검증(서버 845 가드) — 봉인에 쓴 self.UMK()와 동일 값 해시.
      old_hmk: hashString(self.UMK()),
    }

    try {
      const umkRes = await safeboxClient.post(`/v1/auth/create-recovery-key`, params)
      const { code, data } = umkRes.data
      console.log('umkInfo - ', code, data)

      if (code === 200) {
        const { user_id: userId, user_email: email } = data
        console.log('umkInfo - ', userId, email)
        savePrivateKeyLocally(privateKeyPem)
        return Promise.resolve(code)
      }
      // 845(세션 UMK 세대 불일치)만 code 를 전파 — 호출처(Security.js)가 재입력 유도 분기.
      // 그 외 비-200은 기존 계약대로 falsy 반환(호출처는 일반 실패 토스트).
      if (code === 845) {
        return Promise.resolve(code)
      }
    } catch (e) {
      console.log(' - error : ', e)
    }

    return Promise.resolve(false)
  }

  self.savePrivateKeyLocally = savePrivateKeyLocally

  // Settings 복구정책 전환(NONE/LOCAL/ESCROW) → PATCH /v1/users/me/recovery-option payload 생성.
  // recovery_priv 는 서버 미전송(ZK): LOCAL 은 PEM 파일로 다운로드(자가복구용), ESCROW 는 escrow 봉투에만 포함.
  // wrkpList = GET /v1/users/me/wrkp-public-keys 의 wrkp_public_keys[] — ESCROW 는 새 keypair 회전이라
  // wrkp_pub 보유 전체 ws 를 재escrow 해야 전역 enc_umk 와 정합(design §4 F2).
  // ⚠️ PEM 다운로드는 여기서 하지 않고 {payload, localPem} 만 반환 — caller 가 PATCH 성공 후
  //   self.savePrivateKeyLocally(localPem) 호출(PATCH 실패 시 무효 PEM 다운로드 → 계정 잠금 방지; gemini PR#1251).
  self.buildRecoveryOptionPayload = async (targetOption, wrkpList = []) => {
    if (targetOption === RECOVERY_OPTION.NONE) {
      return { payload: { recovery_option: RECOVERY_OPTION.NONE }, localPem: null }
    }

    await initUMK()
    const { publicKeyPem: recoveryPubPem, privateKeyPem: recoveryPrivPem } = await generateRSAKey()
    const payload = {
      recovery_option: targetOption,
      enc_umk: encryptToBase64(self.UMK(), recoveryPubPem),
      recovery_pub_key: recoveryPubPem,
      // 세션 UMK 신선도 검증(서버 845 가드) — enc_umk 봉인에 쓴 self.UMK()와 동일 값을 해시해야
      // 서버 user.hmk 대조가 성립(UMKHash()는 decryptedUMK temp param 우선이라 봉인값과 다를 수 있어 미사용).
      old_hmk: hashString(self.UMK()),
    }

    let localPem = null
    if (targetOption === RECOVERY_OPTION.LOCAL) {
      // recovery_priv 는 서버 미전송 — PATCH 성공 후 caller 가 PEM 파일로 다운로드.
      localPem = recoveryPrivPem
    } else if (targetOption === RECOVERY_OPTION.ESCROW) {
      payload.escrowed_recovery_keys = buildEscrowedKeysForWorkspaces(recoveryPrivPem, wrkpList)
    }

    return { payload, localPem }
  }

  // register escrow 봉투 생성 — escrow = encrypt(recovery_priv, wrkp_pub) (단수, user_key.escrowed_recovery_key).
  // RegisterMasterPW 마운트에서 캐시한 wrkp_pub(registerWrkpPub) 우선 사용(재fetch 레이스 회피).
  // 캐시가 비어 있으면(타이밍/환경 플래그 차이) wrkp-public-keys 를 직접 조회 — escrow 가 null 로
  // 전송되던 버그 방지(가이드 §4.1: 워크스페이스 복구키 미초기화면 서버가 조용히 스킵).
  const buildRegisterEscrow = async (recoveryPrivPem) => {
    // 1순위: RegisterMasterPW 가 캐시한 wrkp_pub (registerWrkpPub).
    let wrkpPub = getTempEnvParam('registerWrkpPub')
    // 2순위: redux user.wrkp_public_key — 초대(register) 가입은 auth/info 값이 여기 채워진다
    //   (saga user/SET_STATE + landing/account Fix C). 캐시가 비는 경우(WRKP_ESCROW_ENABLED off /
    //   타이밍) 대비한 신뢰 가능한 출처. invitee 는 비멤버라 /me 조회가 막히므로 이 fallback 이 핵심.
    if (!wrkpPub) {
      wrkpPub = reduxStore.getState()?.user?.wrkp_public_key || null
    }
    // 3순위: 멤버인 경우에만 유효한 /me/wrkp-public-keys (invitee 는 405/650).
    if (!wrkpPub) {
      try {
        const {
          data: { data: wrkpData },
        } = await safeboxClient.get('/v1/users/me/wrkp-public-keys')
        const entry = (wrkpData?.wrkp_public_keys || []).find((item) => item.wrkp_public_key)
        wrkpPub = entry?.wrkp_public_key || null
      } catch (err) {
        captureSentryError(err, { area: 'register_escrow_wrkp_fetch' })
      }
    }
    return wrkpPub ? buildEscrow(recoveryPrivPem, wrkpPub) : null
  }

  // register 직후 escrow backfill — 초대 가입은 register 시점에 미가입이라 escrow=null 로 나갔으므로,
  // 가입 완료 응답의 wrkp_public_keys(이제 멤버)로 escrow 를 만들어 POST /v1/users/me/escrowed-keys 저장.
  // recovery_priv 는 registerParams 가 보관한 값(escrow 미작성 ESCROW 가입에서만 존재) → 사용 후 폐기.
  // 응답에 키가 없으면 직접 조회(가입 완료로 멤버십 생겨 조회 가능)로 fallback.
  self.backfillRegisterEscrow = async (wrkpPublicKeys) => {
    const recoveryPriv = self.escrowRecoveryPriv
    if (!recoveryPriv) return
    try {
      let list = wrkpPublicKeys
      if (!(list && list.length)) {
        const {
          data: { data: wrkpData },
        } = await safeboxClient.get('/v1/users/me/wrkp-public-keys')
        list = wrkpData && wrkpData.wrkp_public_keys
      }
      const items = buildEscrowedKeysForWorkspaces(recoveryPriv, list || [])
      if (items.length) {
        await safeboxClient.post('/v1/users/me/escrowed-keys', { items })
      }
    } catch (err) {
      captureSentryError(err, { area: 'register_escrow_backfill' })
    } finally {
      self.escrowRecoveryPriv = null
    }
  }

  self.registerParams = async () => {
    await initUMK()
    const { publicKeyPem, privateKeyPem } = await generateRSAKey()

    self.publicKeyPem = publicKeyPem
    self.privateKeyPem = privateKeyPem

    const reduxState = reduxStore.getState()
    const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')
    const inviteEmail = inviteType?.invitee_email
    const { name } = reduxState.user
    const recoveryOption = reduxState.settings.recoveryOption || RECOVERY_OPTION.NONE

    // recovery key 생성.
    const encPrivateKey = await encryptVaultBlob(
      getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
      async () => encryptAESByKey(self.privateKeyPem, self.UMK()),
      { plaintext: self.privateKeyPem, kdf: VAULT_KDF_PBKDF2, keyMaterial: self.UMK() },
    )
    const { publicKeyPem: recoveryPublicKeyPem, privateKeyPem: recoveryPrivateKeyPem } =
      await generateRSAKey()
    const encUMKForRecovery = encryptToBase64(self.UMK(), recoveryPublicKeyPem)
    const decUMKForRecovery = decryptFromBase64(encUMKForRecovery, recoveryPrivateKeyPem)

    let equalKey = false
    if (decUMKForRecovery === self.UMK()) {
      equalKey = true
    }

    // PR1: recovery_option(NONE/LOCAL/ESCROW)별 신모델 자료 구성.
    // - NONE: 복구 자료 없음. LOCAL: enc_umk+recovery_pub + PEM 로컬 다운로드.
    // - ESCROW: enc_umk+recovery_pub + escrow=encrypt(recovery_priv, wrkp_pub).
    // recovery_priv 평문은 서버에 전송하지 않는다(신모델 ZK). email 옵션 제거(Q1).
    let recoveryPubKeyParam = null
    let encUMKParam = null
    let escrowedRecoveryKeyParam = null
    if (
      equalKey &&
      (recoveryOption === RECOVERY_OPTION.LOCAL || recoveryOption === RECOVERY_OPTION.ESCROW)
    ) {
      recoveryPubKeyParam = recoveryPublicKeyPem
      encUMKParam = encUMKForRecovery
      if (recoveryOption === RECOVERY_OPTION.LOCAL) {
        savePrivateKeyLocally(recoveryPrivateKeyPem)
      } else if (recoveryOption === RECOVERY_OPTION.ESCROW) {
        // 캐시된 wrkp_pub 우선, 없으면 직접 조회 — escrow 가 null 로 전송되던 버그 방지(§4.1).
        escrowedRecoveryKeyParam = await buildRegisterEscrow(recoveryPrivateKeyPem)
        // 초대 가입은 register 시점에 미가입이라 wrkp_pub 조회 불가 → escrow=null.
        // 가입 완료 응답의 wrkp_public_keys 로 직후 backfill 하기 위해 recovery_priv 를 보관한다
        // (메모리 한정, backfillRegisterEscrow 에서 사용 후 즉시 폐기 — ZK 유지).
        if (!escrowedRecoveryKeyParam) {
          self.escrowRecoveryPriv = recoveryPrivateKeyPem
        }
      }
    }

    console.log('self.UMKHash() - ', self.UMKHash())
    // Phase 2.5: 초대 수락 흐름 — authUuid가 있으면 우선 사용, 없으면 inviteEmail로 fallback.
    const inviteAuthUuid = store.get('app.user.authUuid')
    return {
      user: {
        user_email: inviteEmail,
        fingerprint: hashString(inviteAuthUuid || inviteEmail),
        hmk: self.UMKHash(),
      },
      user_key: {
        public_key: self.publicKeyPem,
        key: forge.util.encode64(encPrivateKey),
        recovery_pub_key: recoveryPubKeyParam,
        enc_umk: encUMKParam,
        escrowed_recovery_key: escrowedRecoveryKeyParam,
      },
      user_profile: {
        first_name: '',
        last_name: name?.trim()?.length > 0 ? name : store.get('app.user.name'),
      },
      recovery_option: recoveryOption,
      send_mail_option: false,
    }
  }

  self.register = async () => {
    const { publicKeyPem, privateKeyPem } = await generateRSAKey()

    self.publicKeyPem = publicKeyPem
    self.privateKeyPem = privateKeyPem

    const reduxState = reduxStore.getState()
    const { email, name } = reduxState.user
    const recoveryOption = reduxState.settings.recoveryOption || RECOVERY_OPTION.NONE
    const storeName = store.get('app.user.name')

    // 가입은 암호화가 서버 호출보다 **먼저**라 포맷 값이 아직 없다 — 로그인 전이므로
    // `tempEnvParam` 이 비어 있고 갈림길이 구 포맷으로 떨어뜨린다. **그것이 의도다**
    // (설계 D3): 신규 가입자는 워크스페이스가 없어 포맷 판정 자체가 성립하지 않는다.
    // 여기서 만든 값은 PR 7 이 첫 언락에 변환한다.
    const encPrivateKey = await encryptVaultBlob(
      getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
      async () => encryptAESByKey(self.privateKeyPem, self.UMK()),
      { plaintext: self.privateKeyPem, kdf: VAULT_KDF_PBKDF2, keyMaterial: self.UMK() },
    )

    // recovery key 생성.
    const { publicKeyPem: recoveryPublicKeyPem, privateKeyPem: recoveryPrivateKeyPem } =
      await generateRSAKey()

    const encUMKForRecovery = encryptToBase64(self.UMK(), recoveryPublicKeyPem)

    const decUMKForRecovery = decryptFromBase64(encUMKForRecovery, recoveryPrivateKeyPem)

    let equalKey = false
    if (decUMKForRecovery === self.UMK()) {
      equalKey = true
    }

    // PR1: recovery_option별 신모델 자료 (registerParams와 동일 로직).
    let recoveryPubKeyParam = null
    let encUMKParam = null
    let escrowedRecoveryKeyParam = null
    if (
      equalKey &&
      (recoveryOption === RECOVERY_OPTION.LOCAL || recoveryOption === RECOVERY_OPTION.ESCROW)
    ) {
      recoveryPubKeyParam = recoveryPublicKeyPem
      encUMKParam = encUMKForRecovery
      if (recoveryOption === RECOVERY_OPTION.LOCAL) {
        savePrivateKeyLocally(recoveryPrivateKeyPem)
      } else if (recoveryOption === RECOVERY_OPTION.ESCROW) {
        // 캐시된 wrkp_pub 우선, 없으면 직접 조회 — escrow 가 null 로 전송되던 버그 방지(§4.1).
        escrowedRecoveryKeyParam = await buildRegisterEscrow(recoveryPrivateKeyPem)
      }
    }

    setTempEnvParam('decryptedUMK', self.UMK())
    console.log('self.UMKHash() - ', self.UMKHash())

    // Phase 2.5: 신규 회원가입 흐름 — 첫 로그인 전에는 authUuid가 claim_token 콜백에서 저장돼 있을 수 있음.
    const registerAuthUuid = store.get('app.user.authUuid')
    const registerEmail = email || store.get('app.user.email')
    const params = {
      user: {
        user_email: registerEmail,
        fingerprint: hashString(registerAuthUuid || registerEmail),
        hmk: self.UMKHash(),
      },
      user_key: {
        public_key: self.publicKeyPem,
        key: forge.util.encode64(encPrivateKey),
        recovery_pub_key: recoveryPubKeyParam,
        enc_umk: encUMKParam,
        escrowed_recovery_key: escrowedRecoveryKeyParam,
      },
      user_profile: {
        first_name: '',
        last_name: name?.trim()?.length > 0 ? name : storeName,
      },
      recovery_option: recoveryOption,
      send_mail_option: false,
    }

    try {
      if (equalKey === true) {
        const umkRes = await safeboxClient.post(`/v1/auth/register`, params)
        const { code, data, message } = umkRes.data
        console.log('umkInfo - ', code, data)
        if (code === 200) {
          sendSafeboxData(data)
          const saveOptions = {}
          if (options.duration != null) {
            saveOptions.duration = options.duration
          }
          if (options.warningDuration != null) {
            saveOptions.warningDuration = options.warningDuration
          }
          setUMKToLocal(self.UMK(), saveOptions)

          const locale = store.get('app.settings.locale')

          await SettingsService.put({
            local: locale ? locale.substring(0, 2) : 'en',
            autoLock: '10',
            recoveryKeyToEmail: false,
          })

          // WRKP silent flow 트리거 — 첫 owner는 wrkp_pub=null로 응답되어 silent (a) init 발동
          triggerWrkpSilentFlows({
            wrkpPublicKeys: data?.wrkp_public_keys,
            wrkpAdminStatus: data?.wrkp_admin_status,
            umk: self.UMK(),
            privateKeyPem: self.privateKeyPem,
            userRole: data?.role,
            context: 'register',
          })

          return Promise.resolve(true)
        }

        const err = new Error(message)
        err.code = code
        return Promise.reject(err)
      }

      errorBase({
        message: i18n.t('workspace.error.decryptFailed'),
        type: 'error',
      })
      return Promise.reject(new Error('decrypt failed'))
    } catch (e) {
      console.log(' - umkRegister error : ', e)
    }

    return Promise.resolve(false)
  }

  self.changePW = async (newPassword, selfInit, wasTemp = false, fromSettings = false) => {
    if (selfInit === true) {
      await initUMK()
    }

    const reduxState = reduxStore.getState()
    const { email } = reduxState.user

    const oldDecryptedUMK = getTempEnvParam('decryptedUMK')
    if (options.base64PrivateKey != null) {
      if (oldDecryptedUMK == null) {
        const decPrivateKey = forge.util.decode64(options.base64PrivateKey)
        self.privateKeyPem = await decryptAESByKey(decPrivateKey, this.UMK())
      } else {
        const encPrivateKey = forge.util.decode64(options.base64PrivateKey)
        self.privateKeyPem = await decryptAESByKey(encPrivateKey, oldDecryptedUMK)
      }
    }

    assertPemPrivateKey(self.privateKeyPem, 'changePW')
    // 새 비밀번호로 개인키를 다시 봉인한다 — 저엔트로피 파생이라 kdf=1.
    const encPrivateKey = await encryptVaultBlob(
      getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
      async () => encryptAESByKey(self.privateKeyPem, newPassword),
      { plaintext: self.privateKeyPem, kdf: VAULT_KDF_PBKDF2, keyMaterial: newPassword },
    )
    const base64EncPrivateKey = forge.util.encode64(encPrivateKey)

    const cachedRecoveryPubKey = getTempEnvParam('base64recoveryPublicKey')
    const recoveryPubKeyOwner = getTempEnvParam('recoveryPubKeyOwner')
    const activeAccount = store.get('app.user.email') || email
    const recoveryPubKeyTrusted =
      !!cachedRecoveryPubKey && !!recoveryPubKeyOwner && recoveryPubKeyOwner === activeAccount

    const recoveryPublicKeyPem = recoveryPubKeyTrusted ? cachedRecoveryPubKey : null
    const newEncUMK = () => {
      if (recoveryPublicKeyPem) {
        return encryptToBase64(newPassword, recoveryPublicKeyPem)
      }
      return null
    }

    console.log('self.UMKHash() - ', self.UMKHash())

    const persistedRecoveryOption = reduxState.user && reduxState.user.recovery_option
    let escrowedRecoveryKeysParam = null
    let rotatedRecoveryPubKey = null
    let rotatedNewEncUmk = null
    if (wasTemp && persistedRecoveryOption === RECOVERY_OPTION.ESCROW) {
      const { publicKeyPem: newRecPub, privateKeyPem: newRecPriv } = await generateRSAKey()
      rotatedRecoveryPubKey = newRecPub
      rotatedNewEncUmk = encryptToBase64(newPassword, newRecPub) // = encrypt(새 UMK, 새 recovery_pub)
      try {
        const {
          data: { data: wrkpData },
        } = await safeboxClient.get('/v1/users/me/wrkp-public-keys')
        escrowedRecoveryKeysParam = buildEscrowedKeysForWorkspaces(
          newRecPriv,
          wrkpData && wrkpData.wrkp_public_keys,
        )
      } catch (err) {
        // escrow 누락 시 백엔드 SAFEBOX_828로 차단(아래 code!==200) → 사용자에게 에러 표시(무음 실패 아님).
        captureSentryError(err, { area: 'changepw_escrow_rotation' })
      }
    }

    // Phase 2.5: 비밀번호 변경 시 fingerprint도 authUuid 기반으로 생성 (로그인과 동일 정책).
    const changePwAuthUuid = store.get('app.user.authUuid')
    const params = {
      fingerprint: hashString(changePwAuthUuid || email),
      old_hmk: self.UMKHash(),
      new_hmk: hashString(newPassword),
      new_enc_priv: base64EncPrivateKey,
      new_enc_umk: rotatedNewEncUmk || newEncUMK(),
    }
    // escrowed_recovery_keys 는 ESCROW was_temp 회전에서 값이 만들어진 경우에만 동봉 —
    // LOCAL/NONE/자발적 변경에서는 키 자체를 보내지 않는다 (백엔드 default=None, 누락 시 escrow 미갱신).
    if (escrowedRecoveryKeysParam) {
      params.escrowed_recovery_keys = escrowedRecoveryKeysParam
    }
    // §7.3 회전 시에만 recovery_pub_key/recovery_option 동봉(was_temp 분기에서 백엔드가 rec_public_key 갱신).
    if (rotatedRecoveryPubKey) {
      params.recovery_pub_key = rotatedRecoveryPubKey
      params.recovery_option = RECOVERY_OPTION.ESCROW
    }

    try {
      const umkRes = await safeboxClient.put(`/v1/auth/change-password`, params)
      const { code, data } = umkRes.data
      console.log('umkInfo - ', code, data)
      if (code !== 200) {
        if (code === 828 && fromSettings) {
          // Settings 자발적 변경(fromSettings)에서 828(복구 자료 누락)일 때만 이 안내로 분기 —
          // 여기서는 비밀번호를 변경할 수 없으니 마스터패스워드 잠금화면의
          // "비밀번호 재설정"을 이용하도록 유도. 복구/임시계정 강제변경 진입점은
          // 이미 그 재설정 흐름 안이라 이 안내가 순환적이므로 일반 workspace.error.828로 폴백.
          // 전역 workspace.error.828은 Settings 복구 옵션 변경·관리자 복구 등과 공유하므로 건드리지 않는다.
          errorBase({
            title: i18n.t('login.error.changePWBlockedTitle'),
            content: i18n.t('login.error.changePWBlockedDesc'),
          })
        } else {
          errorBase({
            title: 'Error',
            // 폴백 필수 — i18next 는 키가 없으면 **키 문자열 자체**를 반환하므로, 미등록 코드에서
            // 팝업에 `workspace.error.841` 이 그대로 노출된다(활동 목록 툴팁은 defaultValue: code
            // 로 이미 방어 중 — tableInfo/activity.js). Status enum 이 늘 때마다 3언어를 채우는
            // 것을 강제할 가드가 없어 미등록은 앞으로도 생기므로, 일반 문구로 강등한다.
            // 코드 값은 바로 아래 `throw new Error(code)` 로 Sentry·콘솔에 남아 문의 대응이 가능하다.
            // 배열 폴백은 이 저장소 관례 (transferOwnership.js:53, landing/index.js:486 등).
            /* i18next-extract-disable-next-line */
            content: i18n.t([`workspace.error.${code}`, 'workspace.error.null']),
          })
        }
        throw new Error(code)
      }
      if (code === 200) {
        const { role, user_id: userId, user_public_key: userPublicKey, user_key: userKey } = data
        sendSafeboxData(data)

        console.log('umkInfo - ', role, userId, userPublicKey, userKey)

        self.umk = newPassword
        self.publicKeyPem = userPublicKey
        // 변경 후 base64privateKey(enc_priv) 캐시를 새로 업로드한 값으로 갱신한다.
        // 누락 시: 모달 선로딩이 "이미 있으면 skip"이라 새로고침 없이 재변경하면 옛 enc_priv 가
        // 그대로 남아, 새 비밀번호(decryptedUMK)로 복호화 시 "invalid padding"으로 실패(2회차 변경 버그).
        setTempEnvParam('base64privateKey', base64EncPrivateKey)

        const decPrivateKey = forge.util.decode64(userKey)
        self.privateKeyPem = await decryptAESByKey(decPrivateKey, this.UMK())

        const saveOptions = {}
        if (options.duration != null) {
          saveOptions.duration = options.duration
        }
        if (options.warningDuration != null) {
          saveOptions.warningDuration = options.warningDuration
        }
        setUMKToLocal(self.UMK(), saveOptions)

        // WRKP silent flow 트리거 — 새 UMK + 응답의 wrkp 정보
        // 백엔드 auth_change_password 응답에는 wrkp_admin_status=null (auth_login만 채움)이므로 (c)는 무동작
        triggerWrkpSilentFlows({
          wrkpPublicKeys: data?.wrkp_public_keys,
          wrkpAdminStatus: data?.wrkp_admin_status,
          umk: newPassword,
          privateKeyPem: self.privateKeyPem,
          userRole: role,
          context: 'changePW',
        })

        return Promise.resolve(true)
      }
    } catch (e) {
      console.log(' - umkChangePW error : ', e)
    }

    return Promise.resolve(false)
  }

  self.registerGroup = async () => {
    const { publicKeyPem, privateKeyPem } = await generateRSAKey()
    self.publicKeyPem = publicKeyPem
    self.privateKeyPem = privateKeyPem

    // 🛑 **`self.UMK()` 는 여기서 사용자 UMK 가 아니라 `groupKey` 다.**
    //    이 메서드는 `createGroupObj`(`:1447`)가 만든 **그룹 전용 객체**에서 불린다 —
    //    그 객체는 `umk: groupKey` 로 생성되고(`:1454`), `groupKey = createRsKey()`(`:1448`)
    //    는 `forge.random.getBytesSync(32)`, 즉 **32바이트 완전 난수**다.
    //
    //    초판은 이것을 사용자 UMK 로 착각해 *"저엔트로피 파생이라 kdf=1"* 이라 적고
    //    `VAULT_KDF_PBKDF2` 를 썼다. **설계는 처음부터 kdf=0 이었다**
    //    (`backend/docs/design/vault_v2_format.md` 의 표 — 레코드 필드 값과 같은 줄에
    //    같은 판정으로 적혀 있다). 구현이 설계와 갈린 것이고, 이 커밋이 되돌린다.
    //
    //    난수 32바이트는 늘릴 무작위성이 이미 최대치라 PBKDF2 10만 회가 **보안을 전혀
    //    올리지 않는다.** `resource_secret` 과 같은 성질이므로 같은 판정을 쓴다.
    const encPrivateKey = await encryptVaultBlob(
      getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
      async () => encryptAESByKey(self.privateKeyPem, self.UMK()),
      { plaintext: self.privateKeyPem, kdf: VAULT_KDF_NONE, keyMaterial: self.UMK() },
    )

    const params = {
      name: options.name,
      public_key: self.publicKeyPem,
      enc_private_key: forge.util.encode64(encPrivateKey), // BASE64 Encrypted Group Private Key (RSA)
      enc_group_key: options.encGroupKey,
      // is_manager는 서버 기본값 true 사용 (전송 불필요)
    }

    try {
      const umkRes = await safeboxClient.post(`/v1/admin/groups`, params)
      const { code, data } = umkRes.data
      console.log('create group - ', code, data)
      if (code === 200) {
        self.groupId = data.group_id
        return Promise.resolve(true, umkRes)
      }
      const errMsg = umkRes.data.message
      const err = new Error(errMsg)
      err.code = code
      return Promise.reject(err)
    } catch (e) {
      return Promise.reject(new Error(e))
    }
  }

  self.setGroupInfo = async (groupInfo) => {
    await initUMK()

    const {
      id: groupId,
      groupKey,
      public_key: groupPublicKey,
      enc_private_key: encPrivateKey,
    } = groupInfo

    self.groupId = groupId

    self.umk = groupKey
    self.publicKeyPem = groupPublicKey

    if (encPrivateKey != null) {
      const decPrivateKey = forge.util.decode64(encPrivateKey)
      self.privateKeyPem = await decryptAESByKey(decPrivateKey, this.UMK())
    }

    return Promise.resolve(true)
  }

  // this.saveRSAKey = () => {
  // api로 저장하도록 수정 필요.
  // const encPrivateKey = await encryptAESByKey(this.privateKeyPem, this.UMK());
  // const encPublicKey = await encryptAESByKey(this.publicKeyPem, this.UMK());
  // store.set('app.user.encPrivateKey', encPrivateKey);
  // store.set('app.user.encPublicKey', encPublicKey);
  // }

  // self.loadRSAKey = () => {
  //   // api로 저장하도록 수정 필요.
  //   const encPrivateKey = store.get('app.user.encPrivateKey');
  //   const encPublicKey = store.get('app.user.encPublicKey');
  //   if (encPrivateKey == null || encPublicKey == null) {
  //     throw new Error('RSA key not found');
  //   }
  //   self.privateKeyPem = await decryptAESByKey(encPrivateKey, this.UMK());
  //   self.publicKeyPem = await decryptAESByKey(encPublicKey, this.UMK());
  // }
  const createRsKey = () => {
    return forge.random.getBytesSync(32)
  }

  self.createRSKeyObj = (curEncRsKey) => {
    let rsKey = null
    let encRsKey = null

    if (curEncRsKey == null) {
      rsKey = createRsKey()
      encRsKey = encryptToBase64(rsKey)
    } else {
      encRsKey = curEncRsKey
      rsKey = decryptFromBase64(encRsKey, self.privateKeyPem)
    }

    const rsKeyObj = {
      rsKey,
      encRsKey,
      getEncRsKey: () => {
        return rsKeyObj.encRsKey
      },
      // Base64로 인코딩된 문자열을 반환
      encrypt: async (resource) => {
        // 서버가 지시한 포맷으로 갈라진다 (설계 D3). 값은 로그인 때 보관해둔 것이다.
        //
        // 🛑 갈림길은 **바이트 문자열**을 주고받는다 — base64 는 여기(호출부)에서 씌운다.
        //    판독기가 바이트 문자열을 받고(`services/vaultV2/format.js` 의 decryptVaultBlob),
        //    백엔드도 같은 구조다(`common/vault_v2.py` 가 bytes, base64 는 enc_dec 가).
        //    계약을 호출부마다 다르게 두면 새 포맷 팔을 하나로 만들 수 없다.
        //
        // rsKey 는 32바이트 고엔트로피 랜덤이라 파생을 생략한다 (kdf=0, 설계 D5).
        const utf8res = forge.util.encodeUtf8(resource)
        const encResouceBytes = await encryptVaultBlob(
          getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
          async () => encryptAESByKey(utf8res, rsKeyObj.rsKey),
          { plaintext: utf8res, kdf: VAULT_KDF_NONE, keyMaterial: rsKeyObj.rsKey },
        )
        return forge.util.encode64(encResouceBytes)
      },
      decrypt: async (encResouce) => {
        const encResouceBytes = forge.util.decode64(encResouce)
        const resource = await decryptAESByKey(encResouceBytes, rsKeyObj.rsKey)
        const decUtf8res = forge.util.decodeUtf8(resource)
        return decUtf8res
      },

      // 암호화된 바이트(Byte) 배열을 그대로 반환
      // 바이너리 데이터(바이트 배열)를 사람이 읽을 수 있는 텍스트로 강제로 변환해서 출력
      encryptBytes: async (resource) => {
        const encResouceBytes = await encryptAESByKey(resource, rsKeyObj.rsKey)
        return encResouceBytes
      },
      decryptBytes: async (encResouceBytes) => {
        const resource = await decryptAESByKey(encResouceBytes, rsKeyObj.rsKey)
        return resource
      },
      md5: (resource) => {
        const md = forge.md.md5.create()
        md.update(resource)
        return md.digest().toHex()
      },
      getEncRsKeyForShare: (targetPublicKeyPem) => {
        const publicKey = forge.pki.publicKeyFromPem(targetPublicKeyPem) // PEM 형식의 공개 키 불러오기
        const encrypted = publicKey.encrypt(rsKeyObj.rsKey, 'RSA-OAEP', oaepOpts)
        return forge.util.encode64(encrypted) // Base64로 인코딩하여 반환
      },
    }

    return rsKeyObj
  }

  self.createGroupObj = async (name) => {
    const groupKey = createRsKey()

    const encGroupKey = encryptToBase64(groupKey)

    const groupOptions = {
      name,
      umk: groupKey,
      isGroup: true,
      encGroupKey,
    }

    const groupRsaObj = new RSAEncDec(groupOptions)
    await groupRsaObj.init()

    return groupRsaObj
  }

  self.getGroupObj = async (groupInfo) => {
    const { enc_group_key: encGroupKey } = groupInfo
    const groupKey = decryptFromBase64(encGroupKey, self.privateKeyPem)

    const gInfo = {
      id: groupInfo.id,
      public_key: groupInfo.public_key,
      enc_private_key: groupInfo.enc_private_key,
      name: groupInfo.name,
      groupKey,
    }

    const groupRsaObj = new RSAEncDec({ isGroup: true })
    await groupRsaObj.setGroupInfo(gInfo)

    return groupRsaObj
  }

  self.getEncUserGroupKeyForAdd = (userPublicKeyPem) => {
    const publicKey = forge.pki.publicKeyFromPem(userPublicKeyPem) // PEM 형식의 공개 키 불러오기
    const groupKey = self.getGroupKey()
    const encrypted = publicKey.encrypt(groupKey, 'RSA-OAEP', oaepOpts)
    return forge.util.encode64(encrypted) // Base64로 인코딩하여 반환
  }

  self.encryptResourceOnce = async (resource) => {
    const rsKey = forge.random.getBytesSync(32)

    const encResouceBytes = await encryptAESByKey(resource, rsKey)
    const encResouce = forge.util.encode64(encResouceBytes)
    const encRsKey = encryptToBase64(rsKey)

    return {
      encResouce,
      encRsKey,
    }
  }

  self.decryptResourceOnce = async (resInfo) => {
    const { encResouce, encRsKey } = resInfo
    const rsKey = decryptFromBase64(encRsKey, self.privateKeyPem)
    const encResouceBytes = forge.util.decode64(encResouce)
    const resource = await decryptAESByKey(encResouceBytes, rsKey)

    return resource
  }

  self.transferResourceOwnership = async (resourceId, newOwnerId) => {
    try {
      const prepareRes = await safeboxClient.post(`/v1/resources/${resourceId}/owner/prepare`, {
        new_owner_user_workspace_id: newOwnerId,
      })

      const { code: prepareCode, data: prepareData } = prepareRes.data

      if (prepareCode !== 200) {
        throw new Error(prepareCode)
      }

      const {
        new_owner_public_key: newOwnerPublicKey,
        has_existing_key: hasExistingKey,
        enc_resource_key: encResourceKey,
      } = prepareData

      const transferParams = {
        new_owner_user_workspace_id: newOwnerId,
        enc_resource_key: encResourceKey,
      }

      if (!hasExistingKey && encResourceKey) {
        const resourceKey = decryptFromBase64(encResourceKey, self.privateKeyPem)

        const publicKey = forge.pki.publicKeyFromPem(newOwnerPublicKey)
        const encryptedForNewOwner = publicKey.encrypt(resourceKey, 'RSA-OAEP', oaepOpts)
        transferParams.enc_resource_key = forge.util.encode64(encryptedForNewOwner)
      }

      const transferRes = await safeboxClient.patch(
        `/v1/resources/${resourceId}/owner`,
        transferParams,
      )

      const { code: transferCode, data: transferData } = transferRes.data

      if (transferCode !== 200) {
        throw new Error(transferCode)
      }

      return Promise.resolve(transferData)
    } catch (e) {
      console.log(' - transferResourceOwnership error : ', e)
      return Promise.reject(e)
    }
  }
}

// =====================================================
// WRKP Silent Flow Helpers
// 설계: frontend/docs/design/wrkp_registration_migration_frontend.md §3.1.1, §5.2
// =====================================================

// 기능 플래그: dev 환경에서 전체 silent flow 동작 검증 후 cleanup PR로 분기 제거 예정 (§13)
// 외부 컴포넌트(AdminUser 등)에서 분기 확인용으로 export
// 2026-05-21: 운영 배포 준비를 위해 OFF.
// PR1: WRKP silent init 의 owner_escrowed_recovery_key + silentBackfillEscrow 가 구모델 encrypt(UMK) 라
// flag 를 false 로 유지했다(dev-enable 시 구모델 escrow 오염 위험).
// PR2: initWrkpForTarget owner escrow=null + silentBackfillEscrow 호출 제거(아래)로 구모델 오염 위험 해소 →
// dev-only 활성. 운영/스테이징은 OFF (그 전엔 ESCROW 노출 시 복구 경로 부재로 복구 불가 사용자 발생 위험).
// PR3(admin ESCROW recovery read) 배포 완료 → 복구 경로 확보 → 스테이징(전 리전)까지 확대.
// 2026-06-16: 스테이징 검증 완료 → 운영(production)까지 확대(전 환경 ON).
// 옵션 노출(RegisterMasterPW escrowAvailable)·silent WRKP init·escrow 봉인 캐시가 모두 이 한 flag 로
// 묶여 일관 동작한다(노출만 켜지고 봉인은 꺼지는 불일치 = escrow null 버그 방지).
// jest 는 setupTests 가 REACT_APP_TARGET='japan-staging' 강제 → 이제 기본 ON.
const WRKP_ESCROW_DISABLED_TARGETS = []
export const WRKP_ESCROW_ENABLED = !WRKP_ESCROW_DISABLED_TARGETS.includes(
  process.env.REACT_APP_TARGET,
)

// 사용자 복구 정책 (백엔드 recovery_option enum 정합 — NONE/LOCAL/ESCROW)
export const RECOVERY_OPTION = { NONE: 'NONE', LOCAL: 'LOCAL', ESCROW: 'ESCROW' }

const WRKP_RSA_KEY_BITS = 4096
const HYBRID_VERSION = 1
const HYBRID_AES_KEY_BYTES = 32
const HYBRID_IV_BYTES = 12
const HYBRID_TAG_BYTES = 16

// 모듈 레벨 RSA 키쌍 생성 — RSAEncDec 클로저 외부에서 호출 가능
// 기존 RSAEncDec 내부 generateRSAKey와 동일 패턴 (forge.pki.rsa.generateKeyPair)
const generateRsaKeyPair = (bits) =>
  new Promise((resolve, reject) => {
    forge.pki.rsa.generateKeyPair({ bits, workers: 2 }, (err, keypair) => {
      if (err) {
        reject(err)
        return
      }
      resolve({
        publicKeyPem: forge.pki.publicKeyToPem(keypair.publicKey),
        privateKeyPem: forge.pki.privateKeyToPem(keypair.privateKey),
      })
    })
  })

// AES-GCM hybrid encryption (Bitwarden 패턴, 클라이언트 단독 포맷)
// 백엔드는 HybridEncryptedPayload를 JSON 문자열로 저장만 함 (Zero-Knowledge)
// 기존 encryptAES/decryptAES (AES-CBC, PBKDF2)와 별개 — WRKP escrow 전용
export const hybridEncrypt = (plaintext, rsaPublicKeyPem) => {
  const aesKey = forge.random.getBytesSync(HYBRID_AES_KEY_BYTES)
  const iv = forge.random.getBytesSync(HYBRID_IV_BYTES)

  const cipher = forge.cipher.createCipher('AES-GCM', aesKey)
  cipher.start({ iv, tagLength: HYBRID_TAG_BYTES * 8 })
  cipher.update(forge.util.createBuffer(plaintext, 'utf8'))
  cipher.finish()
  const ciphertext = cipher.output.getBytes()
  const tag = cipher.mode.tag.getBytes()

  const publicKey = forge.pki.publicKeyFromPem(rsaPublicKeyPem)
  const encryptedAesKey = publicKey.encrypt(aesKey, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() },
  })

  return {
    version: HYBRID_VERSION,
    encrypted_aes_key: forge.util.encode64(encryptedAesKey),
    encrypted_data: forge.util.encode64(iv + ciphertext + tag),
  }
}

export const hybridDecrypt = (payload, rsaPrivateKeyPem) => {
  if (payload.version !== HYBRID_VERSION) {
    throw new Error(`Unsupported hybrid payload version: ${payload.version}`)
  }

  const privateKey = forge.pki.privateKeyFromPem(rsaPrivateKeyPem)
  const aesKey = privateKey.decrypt(forge.util.decode64(payload.encrypted_aes_key), 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() },
  })

  const raw = forge.util.decode64(payload.encrypted_data)
  const iv = raw.substring(0, HYBRID_IV_BYTES)
  const ciphertext = raw.substring(HYBRID_IV_BYTES, raw.length - HYBRID_TAG_BYTES)
  const tag = raw.substring(raw.length - HYBRID_TAG_BYTES)

  const decipher = forge.cipher.createDecipher('AES-GCM', aesKey)
  decipher.start({ iv, tagLength: HYBRID_TAG_BYTES * 8, tag: forge.util.createBuffer(tag) })
  decipher.update(forge.util.createBuffer(ciphertext))
  const pass = decipher.finish()
  if (!pass) throw new Error('hybridDecrypt: integrity check failed')

  return decipher.output.toString('utf8')
}

// 본인 wrkp_priv PEM 평문 복원
// GET /v1/admin/recovery/wrkp/my-copy → JSON.parse → hybridDecrypt
export const decryptMyWrkpPrivateKey = async ({ privateKeyPem }) => {
  if (!privateKeyPem) {
    throw new Error('privateKeyPem required for decryptMyWrkpPrivateKey')
  }

  const {
    data: {
      data: { encrypted_private_key: encryptedPrivateKeyJson },
    },
  } = await safeboxClient.get('/v1/admin/recovery/wrkp/my-copy')

  if (!encryptedPrivateKeyJson) {
    throw new Error('encrypted_private_key not in response')
  }

  let payload
  try {
    payload = JSON.parse(encryptedPrivateKeyJson)
  } catch (err) {
    throw new Error(`hybrid payload JSON parse failed: ${err.message}`)
  }

  return hybridDecrypt(payload, privateKeyPem)
}

// (a) Silent WRKP 초기화 — admin/owner 세션
// wrkp_public_key=null 워크스페이스 발견 시 4096-bit RSA 생성 + holders 등록 + escrow 저장 (atomic)
const initWrkpForTarget = async ({ target, context, retried }) => {
  try {
    const { publicKeyPem: wrkpPub, privateKeyPem: wrkpPriv } =
      await generateRsaKeyPair(WRKP_RSA_KEY_BITS)

    const {
      data: {
        data: { admin_public_keys: adminKeys },
      },
    } = await safeboxClient.get('/v1/admin/recovery/wrkp/admin-public-keys')

    const holders = adminKeys.map((admin) => ({
      user_workspace_id: admin.user_workspace_id,
      encrypted_wrkp_private_key: hybridEncrypt(wrkpPriv, admin.public_key),
    }))

    // PR2: owner escrow 는 WRKP 초기화 시점이 아니라 그 사용자의 ESCROW enrollment(Settings→SERVER) 시점에
    // 기록한다(신모델 escrow=encrypt(recovery_priv) 는 로그인/init 시점에 recovery_priv 미가용). null 전송 →
    // 백엔드 initialize_wrkp 가 owner escrow 저장 skip(InitializeWrkpModel.owner_escrowed_recovery_key Optional).
    await safeboxClient.post('/v1/admin/recovery/wrkp', {
      wrkp_public_key: wrkpPub,
      holders,
      owner_escrowed_recovery_key: null,
    })
  } catch (err) {
    // R9: SAFEBOX_825 (holder completeness 위반) — 동시 admin 임명 케이스. 1회 재시도
    const status = err?.response?.data?.code || err?.code
    if (!retried && status === 825) {
      await initWrkpForTarget({ target, context, retried: true })
      return
    }
    captureSentryError(err, {
      area: 'wrkp_silent_init',
      context,
      workspace_id: target.workspace_id,
      user_workspace_id: target.user_workspace_id,
      retried,
    })
  }
}

const silentInitializeWrkp = async ({ wrkpPublicKeys, userRole, context }) => {
  // member skip (POST /wrkp 자체가 admin/owner only)
  if (userRole !== 'admin' && userRole !== 'owner') return
  const targets = wrkpPublicKeys.filter((item) => item.wrkp_public_key === null)
  if (!targets.length) return

  await Promise.allSettled(
    targets.map(async (target) => {
      await initWrkpForTarget({ target, context, retried: false })
    }),
  )
}

// (b) silent 자동 백필(구모델 encrypt(UMK))은 PR2 에서 제거됨 — 신모델 escrow=encrypt(recovery_priv) 는
// 로그인 시점에 recovery_priv 미가용이라 자동 백필 불가(design §2.1 R2/F3). escrow_check_pending ws 는
// 사용자의 명시적 Settings→SERVER 전환(전체 ws 재escrow)에서 충족. 신규/누락 ws 백필은 PR3 후속.

// (c) BUG E pending holder 자동 배포 — admin/owner 세션
// holders API는 단일 holder만 받으므로 N명에 대해 N회 호출
const silentDistributePendingHolders = async ({ wrkpAdminStatus, privateKeyPem, context }) => {
  if (!wrkpAdminStatus || !wrkpAdminStatus.pending_holder_targets?.length) return

  let myWrkpPriv
  try {
    myWrkpPriv = await decryptMyWrkpPrivateKey({ privateKeyPem })
  } catch (err) {
    captureSentryError(err, {
      area: 'wrkp_holder_distribution',
      context,
      step: 'decrypt_my_wrkp_priv',
      pending_count: wrkpAdminStatus.pending_holders_count,
    })
    return
  }

  await Promise.allSettled(
    wrkpAdminStatus.pending_holder_targets.map(async (target) => {
      try {
        await safeboxClient.post('/v1/admin/recovery/wrkp/holders', {
          target_user_workspace_id: target.user_workspace_id,
          encrypted_wrkp_private_key: hybridEncrypt(myWrkpPriv, target.user_public_key),
        })
      } catch (err) {
        captureSentryError(err, {
          area: 'wrkp_holder_distribution',
          context,
          target_user_workspace_id: target.user_workspace_id,
        })
      }
    }),
  )
}

// 진입점: register/login/changePW/loginByInvite 응답 직후 fire-and-forget으로 호출
// 호출자는 await 하지 않음 (메인 화면 진입 차단 X)
// R12 대응: (a) 선행 await 후 (c) — admin이 silent init하면 본인이 holder가 되어 wrkp_priv 풀 수 있음.
// (b) silent 자동 백필은 PR2 에서 제거(위 주석) — 구모델 escrow 오염 차단.
export const triggerWrkpSilentFlows = async ({
  wrkpPublicKeys = [],
  wrkpAdminStatus = null,
  umk,
  privateKeyPem,
  userRole,
  context,
}) => {
  if (!WRKP_ESCROW_ENABLED) return
  if (!umk || (!wrkpPublicKeys.length && !wrkpAdminStatus)) return

  try {
    await silentInitializeWrkp({ wrkpPublicKeys, userRole, context })
    await silentDistributePendingHolders({ wrkpAdminStatus, privateKeyPem, context })
  } catch (err) {
    captureSentryError(err, {
      area: 'wrkp_silent_orchestrator',
      context,
    })
  }
}
